import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, catchError, map, take } from 'rxjs';

import { Observable, of, ReplaySubject } from 'rxjs';
import { IUserOrganisation, UserInfo, UserManagementService } from '../../shared/apis/UserMgtAPI/index';
import { IEntity } from '../interfaces/entity';

import { MatDialog } from '@angular/material/dialog';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from '../../components/messageBox/messageBox.component';

import { ToastrService } from 'ngx-toastr';
import { UserService } from './user.service';
import { KeyValuePair } from '../models/keyvaluepair';
import { IDataService } from './data.service';
import { UserOrganisation } from '../models/user';

export enum enumUserRole {
  Admin,
  ReadOnly,
  SearchOnly,
  SysAdmin,
  User,
  OrgManager,
  SubjectAlertImport,
  MultiOrgAdmin,
}

@Injectable({ providedIn: 'root' })
export class UserMgtService implements IDataService {

  userRoles = [
    { name: 'Admin', value: 'admin', role: enumUserRole.Admin },
    { name: 'Organisation Admin', value: 'orgmanager', role: enumUserRole.OrgManager },
    { name: 'User', value: 'user', role: enumUserRole.User },
    { name: 'Read Only', value: 'readonly', role: enumUserRole.ReadOnly },
    { name: 'Search Only', value: 'searchonly', role: enumUserRole.SearchOnly },
    { name: 'Subject Alert Import Only', value: 'subjectalertimport', role: enumUserRole.SubjectAlertImport },
    { name: 'Multi-Organisation Admin', value: 'multiorgadmin', role: enumUserRole.MultiOrgAdmin },
  ];

  users: UserInfo[];
  private routeParams: any;
  public onChanged: ReplaySubject<UserInfo[]> = new ReplaySubject<UserInfo[]>(1, 100);
  public onSelected: BehaviorSubject<any> = new BehaviorSubject({});

  constructor(public client: UserManagementService,
    public userService: UserService,
    public dialog: MatDialog,
    private toastr: ToastrService,
  ) {
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    this.routeParams = route.params;
    {
      return new Promise<void>((resolve, reject) => {
        // console.log('Resolver To get all Users');
        Promise.all([
          this.getUsers(),
        ]).then(
          ([data]) => {
            //  console.log('Resolver To get all Users - Done');
            resolve();
          },
          reject,
        );
      });
    }
  }

  getUsers() {
    // console.log('get all Users from Phoenix WebAPI');
    const defaultUserRole = this.userRoles.find((i) => i.value === 'user');

    this.client.getProductUsers().subscribe((users: UserInfo[]) => {

      // Check UserOrganisation array.
      // make sure role is added to the incoming users based on privilegeString
      users.forEach((userInfo) => {
        let roleValue = userInfo.privilegeString ? userInfo.privilegeString.trim().toLowerCase() : 'user';

        // make sure that if the user is marked as admin by ICS, the role is correctly set to admin
        if (userInfo.isAdmin) {
          roleValue = 'admin';
        }
        userInfo.role = this.userRoles.find((i) => i.value.toLocaleLowerCase() === roleValue.toLowerCase()) || defaultUserRole;
      });

      // stash list of users - we need this so we can apply client side validation checks
      this.users = users;
      // notify listeners of change
      this.onChanged.next(users);
    });
  }

  getCustomerUsers(): Observable<UserInfo[]> {
    return this.client.getCustomerUsers();
  }

  // MRD - something screwy going on with the observable return types, but no matter, we just want to know when it is finished so...
  save(user: UserInfo): Observable<UserInfo | void> {
    // Save changes
    if (user.userUniqueId == null) {
      return this.client.createNewUser(user, 'response').pipe(
        take(1),
        map((d) => {
          // console.log('New User created');
          this.toastr.success('User created successfully', 'Information');
          this.getUsers();
          return d.body;
        }), catchError((e) =>
          of(this.errorHandler(e.error, 'error updating user : ' + e.error)),
        ));
    }
    else {
      return this.client.updateUser(user).pipe(
        take(1),
        map((d) => {
          // console.log('User Updated OK');
          this.toastr.success('User updated successfully', 'Information');
          this.getUsers();
          return d;
        }), catchError((e) =>
          of(this.errorHandler(e.error, 'error updating user : ' + e.error)),
        ));
    }
  }

  errorHandler(message: string, logMessage?: string) {
    if (logMessage) {
      // console.log(logMessage);
    }
    // Show error message with OK button
    const info = new MessageBoxParameters(message, 'Error', MessageBoxType.Ok);
    this.dialog.open(MessageBoxComponent, { data: info }).afterClosed().subscribe((d) => {
    });
    this.getUsers();
  }

  resetPassword(user: UserInfo) {
    // reset password
    // This will create email to send to user with new password.
    this.client.resetPassword(user).subscribe(
      (d) => {
        // console.log('user\'s password reset requested.');
        this.toastr.success('User password reset submitted', 'Information');
      },
      (e) => {
        console.log('Failed to reset user password : ' + e.error);
        this.errorHandler(e.error);
      });
  }

  isUnique(val: string, id: number | string, propertyName: string): Observable<boolean> {
    if (val) {
      const item = this.users.find((i) => (i[propertyName]) && (i.userUniqueId !== id)
        && (i[propertyName].toString().toLowerCase().replace(/\s/g, '') === val.toString().toLowerCase().replace(/\s/g, '')));
      return of(item ? false : true);
    }
    else {
      return of(true);
    }
  }

  isUniqueCombination(id: number | string, properties: KeyValuePair[]): Observable<boolean> {
    if (properties.length > 0) {
      if ((!this.users) || (this.users.length === 0)) { return of(false); }
      let matchingRecords = this.users;
      properties.forEach((r) => {
        const val = r.value.toString().toLowerCase().replace(/\s/g, '');
        matchingRecords = matchingRecords.filter((i) => (i[r.key]) && (i.userUniqueId !== id)
          && (i[r.key].toString().toLowerCase().replace(/\s/g, '') === val));
      });
      return of((matchingRecords.length > 0) ? false : true);
    }
    else {
      return of(true);
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
export class UserInfoClass implements UserInfo, IEntity {
  id: number;
  uid: string;

  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  telephone?: string;
  mobile?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  addressLine5?: string;
  postcode?: string;
  loginId?: string;
  data?: string;
  active?: boolean;
  userUniqueId?: string;
  productId?: string;
  isAdmin?: boolean;
  privilegeString?: string;
  extraData?: string;
  canLogin?: boolean;
  role: any;
  avatar: string;
  fullName: string;
  receiveNewReferralNotifications: boolean;
  canViewArchive: boolean;
  hasCasePrepAccess: boolean;
  organisationId: number;
  userOrganisations: IUserOrganisation[] = [];

  constructor(d?: UserInfo) {
    d = d || {};
    this.uid = d.userUniqueId;

    this.firstName = d.firstName || '';
    this.lastName = d.lastName || '';
    this.telephone = d.telephone || '';
    this.mobile = d.mobile || '';
    this.addressLine1 = d.addressLine1 || '';
    this.addressLine2 = d.addressLine2 || '';
    this.addressLine3 = d.addressLine3 || '';
    this.addressLine4 = d.addressLine4 || '';
    this.addressLine5 = d.addressLine5 || '';
    this.postcode = d.postcode || '';
    this.emailAddress = d.emailAddress || '';
    this.active = d.active || true;
    this.loginId = d.loginId || '';

    this.fullName = (d.firstName + ' ' + d.lastName).trim();
    this.avatar = '';

    this.extraData = d.extraData || '';
    this.privilegeString = d.privilegeString || '';
    this.canLogin = d.canLogin || true;
    this.isAdmin = d.isAdmin || false;
    this.productId = d.productId || '';
    this.role = d.role || { value: '' };

    this.receiveNewReferralNotifications = d.receiveNewReferralNotifications || true;
    this.canViewArchive = d.canViewArchive || false;
    this.hasCasePrepAccess = d.hasCasePrepAccess || false;
    this.organisationId = d.organisationId || null;
    this.userOrganisations = d.userOrganisations || [];
  }
}
